import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useOrgData } from '../hooks/useOrgData';
import { KPIS } from '../shared/criticalPath';
import { canCreateProducts } from '../lib/rbac';
import { loadCapaProgress } from '../lib/capa';
import { ReuniaoSemanal } from '../components/ReuniaoSemanal';
import {
  createBrand,
  createProduct,
  loadBrands,
  type BrandRow,
} from '../lib/data';
import type {
  LineStatus,
  Priority,
  ProductSummary,
  ProjectCategory,
  ProjectLifecycle,
} from '../shared/types';
import {
  CATEGORY_LABELS,
  CategoryTag,
  SELECTABLE_CATEGORIES,
  LINE_STATUS_LABEL,
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  PhaseBand,
  PriorityBadge,
  Progress,
  StatusBadge,
  desvioLabel,
  fmtDate,
  metaInfo,
} from '../components/ui';

const STATUS_FILTERS: (LineStatus | 'todos')[] = [
  'todos',
  'atrasado',
  'atencao',
  'no_prazo',
  'concluido',
  'sem_data',
];

const STATUS_SEVERITY: Record<LineStatus, number> = {
  atrasado: 0,
  atencao: 1,
  sem_data: 2,
  pausado: 3,
  no_prazo: 4,
  concluido: 5,
  cancelado: 6,
};

type SortKey = 'prioridade' | 'previsao' | 'desvio' | 'nome';
const PRIORITIES: Priority[] = ['alta', 'media', 'baixa'];

// Filtro por ciclo de vida (visão do funil). "Em andamento" oculta os em espera.
type LifeFilter = 'andamento' | 'espera' | 'concluidos' | 'cancelados' | 'todos';
// Cancelados não aparecem no painel (têm um menu próprio).
const LIFE_FILTERS: { key: LifeFilter; label: string }[] = [
  { key: 'todos', label: 'Todos' },
  { key: 'andamento', label: 'Em andamento' },
  { key: 'espera', label: 'Em espera' },
  { key: 'concluidos', label: 'Concluídos' },
];
const matchesLife = (lc: ProjectLifecycle, f: LifeFilter): boolean => {
  if (f === 'todos') return true;
  if (f === 'andamento') return lc === 'ativo';
  if (f === 'espera') return lc === 'pausado';
  if (f === 'concluidos') return lc === 'concluido';
  return lc === 'cancelado';
};

interface ResumoFilters {
  life: LifeFilter;
  brand: string;
  category: ProjectCategory | 'todas';
  status: LineStatus | 'todos';
  sort: SortKey;
  q: string;
}

// Padrão ao logar/recarregar: todas as marcas, todas as categorias, todos os
// status (o ciclo de vida começa em "Em andamento", ocultando os em espera).
const DEFAULT_FILTERS: ResumoFilters = {
  life: 'todos',
  brand: 'todas',
  category: 'todas',
  status: 'todos',
  sort: 'prioridade',
  q: '',
};

// Guarda os filtros em memória (nível de módulo): ao entrar num projeto e voltar,
// a seleção é preservada; um recarregamento/novo login zera para o padrão acima.
let savedFilters: ResumoFilters | null = null;

export function Resumo() {
  const { currentOrgId, grants, areas, isRealAdmin, adminMode, setAdminMode, userId, fullName } =
    useAuth();
  const { loading, error, summaries, refresh } = useOrgData(currentOrgId);
  const nav = useNavigate();
  const canCreate = grants ? canCreateProducts(grants) : false;
  // Apenas administradores conduzem a reunião semanal.
  const canRunMeeting = isRealAdmin;
  const [showReuniao, setShowReuniao] = useState(false);

  const areaLabel = useMemo(() => {
    const m = new Map(areas.map((a) => [a.key, a.name]));
    return (k: string) => m.get(k) ?? k;
  }, [areas]);

  const [capaProg, setCapaProg] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    if (currentOrgId) loadCapaProgress(currentOrgId).then(setCapaProg);
  }, [currentOrgId, summaries]);

  const init = savedFilters ?? DEFAULT_FILTERS;
  const [showNew, setShowNew] = useState(false);
  const [life, setLife] = useState<LifeFilter>(init.life);
  const [brand, setBrand] = useState(init.brand);
  const [category, setCategory] = useState<ProjectCategory | 'todas'>(init.category);
  const [status, setStatus] = useState<LineStatus | 'todos'>(init.status);
  const [sort, setSort] = useState<SortKey>(init.sort);
  const [q, setQ] = useState(init.q);

  // Preserva a seleção de filtros ao navegar para um projeto e voltar.
  useEffect(() => {
    savedFilters = { life, brand, category, status, sort, q };
  }, [life, brand, category, status, sort, q]);

  const brands = useMemo(
    () => ['todas', ...Array.from(new Set(summaries.map((s) => s.brand))).sort()],
    [summaries],
  );
  const kpis = useMemo(() => KPIS(summaries), [summaries]);
  const altaPrioridade = useMemo(
    () =>
      summaries.filter(
        (s) => s.priority === 'alta' && s.lifecycle === 'ativo' && s.status !== 'concluido',
      ).length,
    [summaries],
  );

  const filtered = useMemo(
    () =>
      summaries.filter(
        (s) =>
          s.lifecycle !== 'cancelado' &&
          matchesLife(s.lifecycle, life) &&
          (brand === 'todas' || s.brand === brand) &&
          (category === 'todas' || s.category === category) &&
          (status === 'todos' || s.status === status) &&
          (q === '' || `${s.brand} ${s.name}`.toLowerCase().includes(q.toLowerCase())),
      ),
    [summaries, life, brand, category, status, q],
  );

  const sortFn = useMemo(() => {
    const byName = (a: ProductSummary, b: ProductSummary) => a.name.localeCompare(b.name);
    const bySeverity = (a: ProductSummary, b: ProductSummary) =>
      STATUS_SEVERITY[a.status] - STATUS_SEVERITY[b.status] || byName(a, b);
    switch (sort) {
      case 'previsao':
        return (a: ProductSummary, b: ProductSummary) =>
          (a.previsaoConclusao ?? '9999').localeCompare(b.previsaoConclusao ?? '9999') || byName(a, b);
      case 'desvio':
        return (a: ProductSummary, b: ProductSummary) => b.desvioDias - a.desvioDias || byName(a, b);
      case 'nome':
        return byName;
      default:
        return (a: ProductSummary, b: ProductSummary) =>
          PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority] || bySeverity(a, b);
    }
  }, [sort]);

  const sorted = useMemo(() => [...filtered].sort(sortFn), [filtered, sortFn]);

  return (
    <>
      <div className="topbar">
        <h1>Painel de Lançamentos</h1>
        <div className="stat-strip">
          <div className={`stat-chip ${altaPrioridade ? 'prio-strip' : ''}`}>
            <span className="n tabular">{altaPrioridade}</span>
            <span className="l">Prioridade alta</span>
          </div>
          <div className={`stat-chip ${kpis.atrasado ? 'warn' : ''}`}>
            <span className="n tabular">{kpis.atrasado}</span>
            <span className="l">Atrasados</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{kpis.atencao}</span>
            <span className="l">Atenção</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{kpis.noPrazo}</span>
            <span className="l">No prazo</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{kpis.concluido}</span>
            <span className="l">Concluídos</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{kpis.total}</span>
            <span className="l">Total</span>
          </div>
        </div>
      </div>

      <div className="content">
        {error && <div className="card card-pad err">{error}</div>}

        {(isRealAdmin || canCreate || canRunMeeting) && (
          <div className="painel-actions">
            {isRealAdmin && (
              <button
                className={`admin-toggle ${adminMode ? 'on' : 'off'}`}
                style={{ marginRight: 'auto' }}
                onClick={() => setAdminMode(!adminMode)}
                title="Alterna entre a visão administrativa completa e a visão de usuário das suas áreas"
              >
                {adminMode
                  ? '🛡️ Modo admin: LIGADO'
                  : '👤 Vendo como usuário das minhas áreas — clique para voltar ao admin'}
              </button>
            )}
            {canRunMeeting && (
              <button className="btn-meeting" onClick={() => setShowReuniao(true)}>
                ▶ Iniciar reunião semanal
              </button>
            )}
            {canCreate && (
              <button className="btn-primary" onClick={() => setShowNew(true)}>
                ＋ Novo projeto
              </button>
            )}
          </div>
        )}

        <div className="card card-pad">
          {/* Botões (menuzinhos) agrupados no topo */}
          <div className="chip-toggle life-toggle" style={{ marginBottom: 8 }}>
            {LIFE_FILTERS.map((f) => (
              <button key={f.key} className={life === f.key ? 'on' : ''} onClick={() => setLife(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="chip-toggle" style={{ marginBottom: 8 }}>
            {STATUS_FILTERS.map((f) => (
              <button key={f} className={status === f ? 'on' : ''} onClick={() => setStatus(f)}>
                {f === 'todos' ? 'Todos' : LINE_STATUS_LABEL[f]}
              </button>
            ))}
          </div>
          <div className="chip-toggle brand-toggle" style={{ marginBottom: 12 }}>
            {brands.map((b) => (
              <button key={b} className={brand === b ? 'on' : ''} onClick={() => setBrand(b)}>
                {b === 'todas' ? 'Todas as marcas' : b}
              </button>
            ))}
          </div>
          {/* Caixas de seleção por último, embaixo */}
          <div className="filters" style={{ paddingTop: 10, borderTop: '1px solid var(--border-soft)' }}>
            <label className="sort-field">
              <span className="muted">Categoria</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as ProjectCategory | 'todas')}
              >
                <option value="todas">Todas as categorias</option>
                {SELECTABLE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="sort-field">
              <span className="muted">Ordenar por</span>
              <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
                <option value="prioridade">Prioridade</option>
                <option value="desvio">Maior atraso</option>
                <option value="previsao">Previsão</option>
                <option value="nome">Nome</option>
              </select>
            </label>
            <input
              type="search"
              placeholder="Buscar projeto…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ marginLeft: 'auto', minWidth: 200 }}
            />
          </div>
        </div>

        {loading ? (
          <div className="center-note">Carregando…</div>
        ) : sorted.length === 0 ? (
          <div className="center-note">Nenhum projeto com esses filtros.</div>
        ) : sort === 'prioridade' ? (
          PRIORITIES.map((p) => {
            const group = sorted.filter((s) => s.priority === p);
            if (!group.length) return null;
            return (
              <section key={p} className="prio-section">
                <div className="prio-section-head">
                  <span className={`prio prio-${p}`}>{PRIORITY_LABELS[p]}</span>
                  <span className="muted">{group.length} projeto(s)</span>
                </div>
                <div className="proj-grid">
                  {group.map((s) => (
                    <ProjectCard
                      key={s.productId}
                      s={s}
                      areaLabel={areaLabel}
                      capa={capaProg.get(s.productId) ?? 0}
                      onOpen={() => nav(`/produto/${s.productId}`)}
                    />
                  ))}
                </div>
              </section>
            );
          })
        ) : (
          <div className="proj-grid">
            {sorted.map((s) => (
              <ProjectCard
                key={s.productId}
                s={s}
                areaLabel={areaLabel}
                capa={capaProg.get(s.productId) ?? 0}
                onOpen={() => nav(`/produto/${s.productId}`)}
              />
            ))}
          </div>
        )}
      </div>

      {showNew && currentOrgId && (
        <NovoProjeto
          orgId={currentOrgId}
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            refresh();
            nav(`/produto/${id}`);
          }}
        />
      )}

      {showReuniao && currentOrgId && (
        <ReuniaoSemanal
          orgId={currentOrgId}
          userId={userId}
          fullName={fullName}
          isAdmin={isRealAdmin}
          summaries={summaries}
          onClose={() => setShowReuniao(false)}
        />
      )}
    </>
  );
}

function NovoProjeto({
  orgId,
  onClose,
  onCreated,
}: {
  orgId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [brandId, setBrandId] = useState<string>('');
  const [newBrand, setNewBrand] = useState('');
  const [name, setName] = useState('');
  const [sapCode, setSapCode] = useState('');
  const [projectType, setProjectType] = useState<'interno' | 'terceirizado'>('interno');
  const [priority, setPriority] = useState<Priority>('media');
  const [category, setCategory] = useState<ProjectCategory>('inovacao');
  const [openedAt, setOpenedAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    loadBrands(orgId).then((bs) => {
      setBrands(bs);
      const first = bs[0];
      setBrandId(first ? first.id : '__new__');
    });
  }, [orgId]);

  const creatingBrand = brandId === '__new__';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) return setErr('Dê um nome ao projeto.');
    setBusy(true);
    try {
      let finalBrandId = brandId;
      if (creatingBrand) {
        const nb = newBrand.trim();
        if (!nb) {
          setBusy(false);
          return setErr('Informe o nome da nova marca.');
        }
        const { error, brand } = await createBrand(orgId, nb);
        if (error || !brand) {
          setBusy(false);
          return setErr(error ?? 'Não foi possível criar a marca.');
        }
        finalBrandId = brand.id;
      }
      const { error, id } = await createProduct({
        orgId,
        brandId: finalBrandId,
        name: name.trim(),
        sapCode: sapCode.trim() || null,
        projectType,
        priority,
        category,
        openedAt: openedAt || null,
      });
      if (error || !id) {
        setBusy(false);
        return setErr(error ?? 'Não foi possível criar o projeto.');
      }
      onCreated(id);
    } catch (e) {
      setBusy(false);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <div className="section-title">
          <h2>Novo projeto</h2>
          <button className="btn-ghost btn-sm" onClick={onClose}>
            Fechar
          </button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="field">
            <span>Tipo de projeto</span>
            <select
              value={projectType}
              onChange={(e) => setProjectType(e.target.value as 'interno' | 'terceirizado')}
            >
              <option value="interno">Interno (fluxo completo — 24 etapas)</option>
              <option value="terceirizado">Terceirizado (fluxo reduzido)</option>
            </select>
          </label>
          <label className="field">
            <span>Marca</span>
            <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
              <option value="__new__">＋ Nova marca…</option>
            </select>
          </label>
          {creatingBrand && (
            <label className="field">
              <span>Nome da nova marca</span>
              <input value={newBrand} onChange={(e) => setNewBrand(e.target.value)} placeholder="Ex.: Trevo" />
            </label>
          )}
          <label className="field">
            <span>Nome do projeto (linha de produto)</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Kefir 500g Garrafa"
              autoFocus
            />
          </label>
          <label className="field">
            <span>Nº do projeto no SAP (opcional)</span>
            <input
              value={sapCode}
              onChange={(e) => setSapCode(e.target.value)}
              placeholder="ex.: 61000255"
            />
          </label>
          <div className="capa-grid">
            <label className="field">
              <span>Prioridade</span>
              <select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABELS[p]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Categoria</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as ProjectCategory)}
              >
                {SELECTABLE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Data de abertura (opcional)</span>
              <input type="date" value={openedAt} onChange={(e) => setOpenedAt(e.target.value)} />
            </label>
          </div>
          <p className="muted" style={{ fontSize: 12.5 }}>
            As 24 etapas (PD1–PD5) e a capa do projeto são criadas automaticamente.
          </p>
          {err && <p className="err">{err}</p>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn-ghost" onClick={onClose}>
              Cancelar
            </button>
            <button className="btn-primary" disabled={busy}>
              {busy ? 'Criando…' : 'Criar projeto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProjectCard({
  s,
  areaLabel,
  capa,
  onOpen,
}: {
  s: ProductSummary;
  areaLabel: (k: string) => string;
  capa: number;
  onOpen: () => void;
}) {
  const inativo = s.lifecycle === 'cancelado';
  const meta = metaInfo(s.desvioMeta);
  return (
    <button
      className={`proj-card prio-border-${s.priority} ${inativo ? 'proj-inativo' : ''}`}
      onClick={onOpen}
    >
      <div className="proj-card-top">
        <PriorityBadge priority={s.priority} />
        <StatusBadge status={s.status} />
      </div>
      <div className="proj-idrow">
        {s.photoUrl ? (
          <img className="proj-thumb" src={s.photoUrl} alt="" loading="lazy" />
        ) : (
          <div className="proj-thumb ph" aria-hidden="true">📦</div>
        )}
        <div className="proj-idtext">
          <div className="proj-brand">
            {s.brand}
            {s.sapCode ? ` – ${s.sapCode}` : ''}
          </div>
          <div className="proj-name">{s.name}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <CategoryTag category={s.category} />
        {s.projectType === 'terceirizado' && <span className="terc-badge sm">Terceirizado</span>}
      </div>
      <div className="proj-phase">
        <PhaseBand summary={s} />
        <span className="muted proj-fase">{s.faseAtual}</span>
      </div>

      {s.proximaEtapa && (
        <div className="proj-next">
          <span className="proj-next-lbl">Próxima</span>
          <span className="proj-next-txt">
            {s.proximaEtapa.name}
            {s.proximaEtapa.areaKeys.length > 0 && (
              <em> · {s.proximaEtapa.areaKeys.map(areaLabel).join(' / ')}</em>
            )}
          </span>
        </div>
      )}

      <div className="proj-bars">
        <div className="mini-bar">
          <span className="mb-l">Projeto</span>
          <Progress value={s.percentConcluido} />
          <span className="mb-v tabular">{Math.round(s.percentConcluido * 100)}%</span>
        </div>
        <div className="mini-bar capa">
          <span className="mb-l">Capa</span>
          <Progress value={capa} />
          <span className="mb-v tabular">{Math.round(capa * 100)}%</span>
        </div>
      </div>

      <div className="proj-foot">
        <span className="proj-meta">
          <span className="tabular">{fmtDate(s.previsaoConclusao)}</span>
          <span
            className="tabular"
            style={{ color: s.desvioDias > 0 ? 'var(--st-atrasada)' : 'var(--ink-faint)' }}
          >
            {desvioLabel(s.desvioDias)}
          </span>
        </span>
      </div>

      {s.targetLaunch && (
        <div className={`proj-metatag ${meta.tone}`}>
          Meta {fmtDate(s.targetLaunch)} · {meta.text}
        </div>
      )}
    </button>
  );
}
